/**
 * Sticky Navigation Frontend JavaScript
 * Discovers anchors, builds navigation list, and highlights active sections.
 *
 * All state (observer, scroll listener, debounce timer, lists, anchors) is
 * scoped to the init() closure so destroy() can fully tear it down on
 * soft-navigation. No `window.modulrStickyNav*` globals.
 */

import { ready } from '../../lib/lifecycle/ready';

function discoverAnchors() {
  const containers = Array.from(document.querySelectorAll('[data-modulr-anchor]'));

  if (containers.length > 0) {
    return containers.map((el) => ({
      id: el.id,
      anchor: el.dataset.modulrAnchor,
      friendlyName: el.dataset.modulrAnchorFriendly || el.dataset.modulrAnchor,
      element: el
    }));
  }

  const anchorTags = Array.from(document.querySelectorAll('a.modulr-anchor[name]'));

  return anchorTags.map((el) => {
    const anchorName = el.getAttribute('name');
    const friendlyName = el.getAttribute('data-friendly-name') || anchorName;

    let container = el.nextElementSibling;
    if (!container || !container.id) {
      container = el.parentElement;
    }

    return {
      id: container?.id || anchorName,
      anchor: anchorName,
      friendlyName: friendlyName,
      element: container || el
    };
  });
}

function buildNavList(navElement, anchors) {
  const ul = navElement.querySelector('ul[data-modulr-anchor-nav]');
  if (!ul) return;

  const existingItems = ul.querySelectorAll('li');
  if (existingItems.length === anchors.length) {
    let matches = true;
    existingItems.forEach((li, index) => {
      const link = li.querySelector('a');
      if (!link || link.dataset.anchor !== anchors[index].anchor) {
        matches = false;
      }
    });
    if (matches) {
      return;
    }
  }

  ul.innerHTML = anchors.map((anchor) => {
    const friendlyName = anchor.friendlyName || anchor.anchor;
    return `
      <li>
        <a href="#${anchor.anchor}"
           data-anchor="${anchor.anchor}"
           aria-label="Navigate to ${friendlyName}">
          <span class="bullet"></span>
          <span class="label">${friendlyName}</span>
        </a>
      </li>
    `;
  }).join('');
}

function updateActiveSection(navElement, anchors, offset, scrollDirection) {
  if (anchors.length === 0) return;

  const viewportCenter = window.scrollY + (window.innerHeight / 2) + offset;

  let activeAnchor = null;

  for (let i = anchors.length - 1; i >= 0; i--) {
    const anchor = anchors[i];
    const rect = anchor.element.getBoundingClientRect();
    const elementTop = rect.top + window.scrollY;

    if (elementTop <= viewportCenter) {
      activeAnchor = anchor;
      break;
    }
  }

  if (!activeAnchor) {
    if (scrollDirection === 'down') {
      activeAnchor = anchors.length > 0 ? anchors[0] : null;
    } else {
      activeAnchor = anchors.length > 0 ? anchors[anchors.length - 1] : null;
    }
  }

  const listItems = navElement.querySelectorAll('li');
  listItems.forEach((li) => {
    const link = li.querySelector('a');
    if (!link) return;

    const anchorName = link.dataset.anchor;

    if (activeAnchor && anchorName === activeAnchor.anchor) {
      li.classList.add('active');
    } else {
      li.classList.remove('active');
    }
  });
}

function initStickyNav(root) {
  const scope = root || document;

  // Closure state — replaces former window.* globals.
  let navLists = document.querySelectorAll('[data-modulr-anchor-nav]');
  if (navLists.length === 0) {
    return undefined;
  }

  let anchors = [];
  let initializing = false;
  let reinitTimeout = null;
  let observer = null;
  let scrollHandler = null;

  function rebuild() {
    navLists = document.querySelectorAll('[data-modulr-anchor-nav]');

    if (navLists.length === 0) return;

    anchors = discoverAnchors();

    if (anchors.length === 0) {
      navLists.forEach((navList) => {
        const navElement = navList.closest('nav');
        if (navElement) {
          navElement.style.display = 'none';
        }
      });
      return;
    }

    if (observer) {
      observer.disconnect();
    }

    navLists.forEach((navList) => {
      const navElement = navList.closest('nav');
      if (!navElement) return;

      const offset = parseInt(navElement.dataset.scrollOffset, 10) || 100;

      buildNavList(navElement, anchors);
      updateActiveSection(navElement, anchors, offset, 'down');
    });

    if (observer) {
      setTimeout(() => {
        if (observer && document.body) {
          observer.observe(scope === document ? document.body : scope, {
            childList: true,
            subtree: true
          });
        }
      }, 100);
    }
  }

  rebuild();

  let scrollTimeout;
  let previousScrollY = window.scrollY;

  scrollHandler = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const currentScrollY = window.scrollY;
      const scrollDirection = currentScrollY > previousScrollY ? 'down' : 'up';
      previousScrollY = currentScrollY;

      navLists.forEach((navList) => {
        const navElement = navList.closest('nav');
        if (!navElement) return;

        const offset = parseInt(navElement.dataset.scrollOffset, 10) || 100;
        updateActiveSection(navElement, anchors, offset, scrollDirection);
      });
    }, 50);
  };

  window.addEventListener('scroll', scrollHandler, { passive: true });

  if (typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver((mutations) => {
      if (initializing) {
        return;
      }

      const isOurMutation = mutations.some((mutation) => {
        const target = mutation.target;
        if (target.nodeType === 1) {
          const navList = target.closest ? target.closest('[data-modulr-anchor-nav]') : null;
          if (navList) {
            return true;
          }
          if (target.hasAttribute && target.hasAttribute('data-modulr-anchor-nav')) {
            return true;
          }
        }
        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType === 1) {
            return node.closest && node.closest('[data-modulr-anchor-nav]');
          }
          return false;
        }) || Array.from(mutation.removedNodes).some((node) => {
          if (node.nodeType === 1) {
            return node.closest && node.closest('[data-modulr-anchor-nav]');
          }
          return false;
        });
      });

      if (isOurMutation) {
        return;
      }

      clearTimeout(reinitTimeout);
      reinitTimeout = setTimeout(() => {
        initializing = true;
        rebuild();
        setTimeout(() => {
          initializing = false;
        }, 100);
      }, 250);
    });

    observer.observe(scope === document ? document.body : scope, {
      childList: true,
      subtree: true
    });
  }

  return function destroy() {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (reinitTimeout) {
      clearTimeout(reinitTimeout);
      reinitTimeout = null;
    }
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
  };
}

ready(initStickyNav, { scope: 'modulr-sticky-nav' });
